// © 2025 GridStorm / Tekivex — All Rights Reserved
// Unauthorized reproduction or distribution is prohibited.
/**
 * @gridstorm/dataflow-sentinel/react — the Sentinel loop as a React hook.
 *
 * Thin wrapper over the framework-agnostic Sentinel controller. Pass the
 * engine's accumulating anomaly-event array, the live row buffer, the config,
 * and (optionally) a pluggable AI enrich provider + notifiers.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { AnomalyEvent, StreamRow } from '@gridstorm/dataflow-core';
import type { Finding, SentinelConfig } from './types.js';
import type { EnrichProvider } from './enrich.js';
import type { Notifier } from './notifier.js';
import { Sentinel } from './sentinel.js';

const MAX_FINDINGS = 100;

export interface UseSentinelOptions {
  enrichProvider?: EnrichProvider;
  notifiers?:      Notifier[];
}

export interface UseSentinelResult {
  findings:   Finding[];
  clear:      () => void;
  suppressed: number;
}

export function useSentinel(
  anomalies: readonly AnomalyEvent[],
  rows:      readonly StreamRow[],
  config:    SentinelConfig,
  opts:      UseSentinelOptions = {},
): UseSentinelResult {
  const [findings, setFindings]     = useState<Finding[]>([]);
  const [suppressed, setSuppressed] = useState(0);

  const rowsRef = useRef(rows); rowsRef.current = rows;

  // Upsert a finding by id (the controller calls onFinding twice: raw + enriched)
  const upsert = useCallback((f: Finding) => {
    setFindings((prev) => {
      const i = prev.findIndex((x) => x.id === f.id);
      if (i >= 0) { const next = prev.slice(); next[i] = f; return next; }
      return [f, ...prev].slice(0, MAX_FINDINGS);
    });
  }, []);

  // One controller instance for the lifetime of the hook.
  const sentinel = useMemo(() => new Sentinel({
    config,
    onFinding:    upsert,
    onSuppressed: () => setSuppressed((s) => s + 1),
    enrichProvider: opts.enrichProvider,
    notifiers:      opts.notifiers,
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }), []);

  // Keep the controller's config / provider / notifiers fresh.
  useEffect(() => { sentinel.setConfig(config); }, [sentinel, config]);
  useEffect(() => { sentinel.setEnrichProvider(opts.enrichProvider); }, [sentinel, opts.enrichProvider]);
  useEffect(() => { sentinel.setNotifiers(opts.notifiers ?? []); }, [sentinel, opts.notifiers]);

  // Process new anomaly events as the array grows.
  useEffect(() => {
    sentinel.ingestMany(anomalies, rowsRef.current);
  }, [sentinel, anomalies]);

  const clear = useCallback(() => {
    setFindings([]);
    setSuppressed(0);
    sentinel.reset();
  }, [sentinel]);

  return { findings, clear, suppressed };
}
